import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter
} from '@chakra-ui/react';
import Select from 'react-select';
import BadgeWithMessage from '../../../../../BadgeWithMessage/BadgeWithMessage';
import CustomChakraButton from '../../../../../CustomChakraButton/CustomChakraButton';
import CustomInput from '../../../../../CustomInput/CustomInput';
import IconComponent from '../../../../../IconComponent/IconComponent';
import ChooseMonths from '../../../../../ChooseMonths/ChooseMonths';
import { DEFAULT_MEAL_FROM_BASE, useAddEditMealPlan } from './useAddEdtiMealPlan';
import { MultipleSelect } from '../../../../../Forms/MultipleSelect/MultipleSelect';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type OptionType = { label: string; value: number };

const AddEditMealPlan = ({ isOpen, onClose }: Props) => {
  const {
    newMealFormData,
    setNewMealFormData,
    currentDate,
    setCurrentDate,
    formattedDate,
    availableMeals,
    availableMealsToSelect,
    getCategoryName,
    mealCategoriesToSelect,
    allergens,
    allergensToSelect,
    mealFromBaseFormData,
    setMealFromBaseFormData,
    mealsForDay,
    uniqueCategories,
    addNewMealToMealList,
    handleAssignMealsFromBase,
    handleDeleteMeal,
    menusToSelect,
    selectedMenuList,
    setSelectedMenuList,
    selectedCategory,
    setSelectedCategory,
    selectedCategoryForBaseMeal,
    setSelectedCategoryForBaseMeal
  } = useAddEditMealPlan();

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} size="70-vw" isCentered>
      <form onSubmit={() => console.log('submit')}>
        <ModalOverlay
          sx={{
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
        />
        <ModalContent>
          <ModalHeader className="flex">
            <div className="basis-5/12 flex flex-col text-grayLight-900 font-semibold text-lg border-r border-grayLight-200">
              <h2 className="font-semibold text-lg">Dodaj do jadłospisu</h2>
              <p className="text-grayLight-600 text-sm font-normal">
                Aby stworzyć jadłospis wybierz datę a następnie dodaj kategorie i przypisz do nich
                dania.
              </p>
            </div>
            <div className="basis-7/12 flex items-center justify-between text-grayLight-700 font-semibold text-xl box-border border-l border-grayLight-200">
              <h2 className="font-semibold text-lg pr-spacing-3xl pl-spacing-3xl">
                Podgląd do jadłospisu
              </h2>
              <h2 className="font-semibold text-lg pr-spacing-3xl pl-spacing-3xl">
                {formattedDate || 'Data'}
              </h2>
            </div>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody className="flex">
            <div className="basis-5/12 border-r border-grayLight-200 flex flex-col gap-spacing-xl pr-spacing-3xl pl-spacing-3xl">
              <div className="flex flex-col gap-spacing-xs">
                <label className="text-sm font-medium text-grayLight-700">
                  Dzień obowiązywania jadłospisu
                </label>
                <div className="w-44">
                  <ChooseMonths
                    hideTodayButton={true}
                    setDate={setCurrentDate}
                    currentDate={currentDate}
                    mode="day"
                  />
                </div>
              </div>

              {/* Dodawanie nowego posiłku */}
              <div className="flex flex-col gap-spacing-xl">
                <div className="flex flex-col pt-spacing-xs pb-spacing-xs border-b border-brand-600">
                  <p className="font-semibold text-lg text-grayLight-900">Stwórz danie</p>
                </div>
                <div className="flex flex-col gap-spacing-xl">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-grayLight-700">
                      Przypisz kategorię
                    </label>
                    <Select<OptionType>
                      required
                      placeholder="Wybierz"
                      value={
                        newMealFormData.courseId
                          ? {
                              label: getCategoryName(newMealFormData.courseId),
                              value: newMealFormData.courseId
                            }
                          : null
                      }
                      onChange={(value) =>
                        setNewMealFormData((prev) => ({ ...prev, courseId: Number(value?.value) }))
                      }
                      options={mealCategoriesToSelect}
                    />
                  </div>
                  <CustomInput
                    type="default"
                    size="md"
                    destructive={false}
                    label="Nazwa*"
                    inputParams={{
                      value: newMealFormData.name,
                      onChange: (e) =>
                        setNewMealFormData((prev) => ({ ...prev, name: e.target.value })),
                      placeholder: 'Wpisz nazwę dania np. Owsianka z owocami'
                    }}
                  />
                  <div className="flex flex-col">
                    <label className="text-grayLight-700 text-sm">Opis</label>
                    <textarea
                      cols={30}
                      rows={5}
                      style={{ resize: 'none' }}
                      value={newMealFormData.description}
                      onChange={(e) =>
                        setNewMealFormData((prev) => ({ ...prev, description: e.target.value }))
                      }
                      maxLength={1000}
                      className="border border-gray-300 rounded-lg p-spacing-lg"
                    />
                    <div className="flex justify-between">
                      <label className="text-grayLight-700 text-sm">maks. 1000 znaków</label>
                      <label className="text-grayLight-700 text-sm">
                        {newMealFormData.description.length}/1000
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-grayLight-700">Alergeny</label>
                    <MultipleSelect
                      options={allergensToSelect}
                      selected={newMealFormData.allergenIds}
                      setSelected={(newIds) =>
                        setNewMealFormData((prev) => ({
                          ...prev,
                          allergenIds: newIds
                        }))
                      }
                      placeholder="Wybierz alergeny"
                      fullWidth
                    />
                  </div>

                  <div className="flex justify-end">
                    <CustomChakraButton
                      buttonProps={{
                        type: 'button',
                        disabled: newMealFormData.name.length === 0,
                        onClick: () => addNewMealToMealList()
                      }}
                      hierarchy="secondaryColor"
                      size="sm"
                      iconPosition="left"
                      icon="plus">
                      Dodaj do jadłospisu
                    </CustomChakraButton>
                  </div>
                </div>

                {/* Dodawanie z bazy */}
                <div className="flex flex-col pt-spacing-xs pb-spacing-xs border-b border-brand-600">
                  <p className="font-semibold text-lg text-grayLight-900">Dodaj dania z bazy dań</p>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-grayLight-700">
                    Wybierz Kategorię posiłku:
                  </label>
                  <MultipleSelect
                    options={mealCategoriesToSelect}
                    selected={selectedCategoryForBaseMeal}
                    setSelected={(selectedIds) => {
                      setSelectedCategoryForBaseMeal(selectedIds);
                    }}
                    placeholder="Wybierz kategorię"
                    fullWidth={true}
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-grayLight-700">Wybierz bazę:</label>
                  <MultipleSelect
                    options={menusToSelect}
                    selected={selectedMenuList}
                    setSelected={setSelectedMenuList}
                    placeholder="Wybierz bazę dań"
                    fullWidth={true}
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-grayLight-700">Wybierz dania</label>
                  <Select
                    isMulti
                    isSearchable={true}
                    placeholder="Wybierz posiłek"
                    value={mealFromBaseFormData.map((meal) => ({
                      label: meal.name,
                      value: meal.id
                    }))}
                    onChange={(value) => {
                      const selectedMeals = value.map((mealValue) => {
                        const meal = availableMeals.find((m) => m.id === mealValue?.value);
                        return meal
                          ? {
                              id: meal.id,
                              name: meal.name,
                              description: meal.description,
                              allergens: meal.allergens,
                              categoryIds: selectedCategoryForBaseMeal
                            }
                          : DEFAULT_MEAL_FROM_BASE;
                      });

                      setMealFromBaseFormData(selectedMeals);
                    }}
                    options={availableMealsToSelect}
                  />
                </div>

                <div className="flex justify-end">
                  <CustomChakraButton
                    hierarchy="secondaryColor"
                    size="sm"
                    iconPosition="left"
                    icon="plus"
                    buttonProps={{
                      type: 'button',
                      disabled: mealFromBaseFormData.length === 0,
                      onClick: () => handleAssignMealsFromBase()
                    }}>
                    Dodaj do jadłospisu
                  </CustomChakraButton>
                </div>
              </div>
            </div>

            {/* Podgląd */}
            <div
              className={`basis-7/12 border-l border-grayLight-200 flex flex-col ${mealsForDay.length === 0 ? ' items-center justify-center' : ''}`}>
              {mealsForDay.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-spacing-xl">
                  <div>
                    <IconComponent iconName="cutlery" className="w-10 h-10" />
                  </div>
                  <p>Tu pojawi się lista dań dodanych do jadłospisu na dany dzień.</p>
                </div>
              ) : (
                <div className="p-spacing-3xl">
                  <ul className="list-disc ml-5 flex flex-col gap-spacing-4xl">
                    {uniqueCategories.map((categoryId) => {
                      if (!categoryId) return null;

                      return (
                        <li
                          key={categoryId}
                          className="text-lg font-semibold list-none text-grayLight-900">
                          <h3 className="text-lg font-semibold">
                            {getCategoryName(categoryId).charAt(0).toUpperCase() +
                              getCategoryName(categoryId).slice(1)}
                          </h3>
                          <ul className="flex flex-col gap-spacing-4xl">
                            {mealsForDay
                              .filter((meal) => meal.courseId === categoryId)
                              .map((meal) => (
                                <li
                                  key={meal.id}
                                  className="text-sm font-medium text-grayLight-700">
                                  <div className="flex flex-col gap-spacing-xs">
                                    <div className="flex justify-between border-b border-brand-300">
                                      <p className="font-normal text-sm text-grayLight-900 pt-spacing-xs pr-spacing-md pb-spacing-xs pl-spacing-md">
                                        {meal.name}
                                      </p>
                                      <button
                                        className="bg-transparent border-none"
                                        type="button"
                                        onClick={() => {
                                          if (!meal.id || !meal?.mealToDayId) return;
                                          handleDeleteMeal(meal?.mealToDayId || meal?.id);
                                        }}>
                                        <IconComponent iconName="xClose" className="w-3 h-3" />
                                      </button>
                                    </div>
                                    <p className="text-grayLight-600 pt-spacing-xs pr-spacing-md pb-spacing-xs pl-spacing-md">
                                      {meal.description}
                                    </p>
                                    <div className="flex gap-spacing-xs flex-wrap">
                                      {meal.allergenIds.map((allergenId) => {
                                        const allergen = allergens.find(
                                          (allergen) => allergen.id === allergenId
                                        );
                                        if (!allergen) return;
                                        return (
                                          <BadgeWithMessage
                                            tooltipLabel={allergen.name}
                                            key={
                                              allergen.id
                                            }>{`${allergen.formalNumber}`}</BadgeWithMessage>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </li>
                              ))}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </ModalBody>

          <ModalFooter className="w-full flex justify-between gap-spacing-lg">
            <CustomChakraButton
              hierarchy="primary"
              size="lg"
              iconPosition="onlyText"
              buttonProps={{
                className: 'basis-5/12',
                type: 'button',
                onClick: onClose
              }}>
              Zakończ
            </CustomChakraButton>
            <div className="basis-7/12"></div>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default AddEditMealPlan;
